import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

const MORNING_SKY = `url(https://t3.ftcdn.net/jpg/06/91/52/16/360_F_691521600_NHuR3Cy4G68NhjfbwOkquuoHyAfx0fFS.jpg) no-repeat`;

const DECIMAL_PATTERN = /^\d*(\.\d{0,2})?$/;

export default function RegisterOpenDialog({
  open,
  onClose,
  onConfirm,
  openedAt,
  initialCashOnHand = 0,
  loading = false,
  modalZIndex,
  locationName,
}) {
  const [cashOnHand, setCashOnHand] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const n = Number(initialCashOnHand);
    if (Number.isNaN(n)) {
      setCashOnHand("");
    } else {
      setCashOnHand(n === 0 ? "0" : String(n));
    }
    setError("");
  }, [open, initialCashOnHand]);

  const handleCashChange = (raw) => {
    if (raw === "" || DECIMAL_PATTERN.test(raw)) {
      setCashOnHand(raw);
      if (error) setError("");
    }
  };

  const handleConfirm = () => {
    if (loading) return;
    if (cashOnHand === "") {
      setError("Enter starting cash on hand");
      return;
    }
    if (!DECIMAL_PATTERN.test(cashOnHand)) {
      setError("Enter a valid number (up to 2 decimals)");
      return;
    }
    const n = Number(cashOnHand);
    if (Number.isNaN(n) || n < 0) {
      setError("Enter a valid amount");
      return;
    }
    onConfirm({ cashOnHand: +n.toFixed(2) });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={modalZIndex != null ? { zIndex: modalZIndex } : undefined}
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <Box
        sx={{
          background: MORNING_SKY,
          backgroundSize: "cover",
          backgroundPosition: "bottom",
          color: "#fff",
          px: 3,
          py: 4,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        <WbSunnyIcon sx={{ fontSize: 28, color: "#00000090" }} />
        <div>
          <Typography
            component="div"
            sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#000000b0" }}
          >
            Open Register{locationName ? ` · ${locationName}` : ""}
          </Typography>
          <Typography
            component="div"
            sx={{ opacity: 0.85, fontSize: "0.8rem", color: "#00000090" }}
          >
            {locationName
              ? `Start a new register period for ${locationName}`
              : "Start a new register period"}
          </Typography>
        </div>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Confirm opening the register for the next period. Starting cash
            defaults to what you closed with last night - adjust if the drawer
            amount changed.
          </Typography>

          <Box
            sx={{
              border: "1px solid rgba(46,125,50,0.35)",
              borderRadius: 2,
              p: 1.25,
              bgcolor: "rgba(46,125,50,0.08)",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ScheduleIcon sx={{ color: "#2e7d32", fontSize: 18 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                New period starts at
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: "#2e7d32" }}
              >
                {openedAt}
              </Typography>
            </Box>
          </Box>

          <TextField
            label="Starting cash on hand"
            value={cashOnHand}
            onChange={(e) => handleCashChange(e.target.value)}
            error={Boolean(error)}
            helperText={
              error ||
              "From last night's close; edit if the drawer no longer matches."
            }
            fullWidth
            size="small"
            disabled={loading}
            inputProps={{ inputMode: "decimal" }}
            placeholder="0.00"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyIcon sx={{ fontSize: 20, color: "#37474f" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fafafa",
                "&.Mui-focused fieldset": { borderColor: "#37474f" },
              },
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid #e0e0e0",
          bgcolor: "#fafafa",
        }}
      >
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <LockOpenIcon />
            )
          }
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {loading ? "Opening…" : locationName ? `Confirm & Open ${locationName}` : "Confirm & Open Register"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
